/**
 * @author Ahmed Samer
 * @date    2021-06-07
 * @filename  featured item carousel
 * @LastModifiedBy Ahmed Samer
 * @Copyright Copyright 2021 by Radivision Inc., CA, USA. All Rights Reserved.
 */
import React, { Component } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import { ConnectedFeaturedItemPanel, FeaturedItemPanelContainer } from "./featured-item-panel";
import { FixedList } from "@radivision/graphql/lib/ts/graphql/fixed-list";
import { graphql, QueryRenderer } from "react-relay";
import { RelayProps } from "../../relay/relay-props";
import { ReadyState } from "../../relay/ready-state";
import { SVG } from "../../component-configuration/svgs";

interface FeaturedItemPanelCarouselProps extends RelayProps {
  /**
   * The list of items in the carousel.
   *
   * @type {GraphQlFixedList}
   */
  list: FixedList;
}

class FeaturedItemPanelCarousel extends Component<FeaturedItemPanelCarouselProps> {
  carouselRef: React.RefObject<any>;

  constructor(props: FeaturedItemPanelCarouselProps) {
    super(props);
    this.carouselRef = React.createRef();
  }

  render() {
    const LIST = this.props.list;
    return (
      <div className="hero-container xxtra featured">
        {LIST?.items?.edges ? (
          <Carousel
            ref={this.carouselRef}
            autoPlay={true}
            infiniteLoop={true}
            interval={10000}
            showIndicators={true}
            stopOnHover={false}
            showArrows={false}
            showThumbs={false}
            renderArrowPrev={(): React.ReactNode => {
              const CAROUSEL = this?.carouselRef?.current;

              return (
                <div className="carousel-control-prev carousel-navigation">
                  <span
                    className="carousel-control-prev-icon right"
                    role="button"
                    data-slide="prev"
                    onClick={() => {
                      if (CAROUSEL) {
                        CAROUSEL.decrement();
                      }
                    }}
                  >
                    {SVG.nextArrow}
                  </span>
                </div>
              );
            }}
            renderArrowNext={(): React.ReactNode => {
              const CAROUSEL = this?.carouselRef?.current;
              return (
                <div className="carousel-control-next carousel-navigation">
                  <span
                    className="carousel-control-next-icon"
                    role="button"
                    data-slide="next"
                    onClick={() => {
                      if (CAROUSEL) {
                        CAROUSEL.increment();
                      }
                    }}
                  >
                    {SVG.nextArrow}
                  </span>
                </div>
              );
            }}
          >
            {LIST?.items?.edges?.map((edge: any, index) => {
              const ID = edge?.listItem?.item?.id;
              const TYPE = edge?.listItem?.item?.__typename.toLowerCase().replace("story", "");
              const PROPS = { [TYPE]: { id: ID } };
              return <FeaturedItemPanelContainer {...PROPS} key={index} />;
            })}
          </Carousel>
        ) : (
          <ConnectedFeaturedItemPanel />
        )}
      </div>
    );
  }
}

export class FeaturedItemPanelCarouselContainer extends Component<FeaturedItemPanelCarouselProps> {
  render() {
    const listId: string = this.props?.list?.id;

    if (listId === undefined || listId === null) {
      return null;
    }

    return (
      <QueryRenderer
        environment={this.props.relay.environment}
        query={graphql`
          query featuredItemCarouselQuery($listId: ID!, $after: String, $first: Int) {
            list(id: $listId) {
              errors {
                created
                id
                location
                type
              }
              hasErrors
              list {
                __typename
                ... on FixedList {
                  id
                  landingPageUrl
                  items(after: $after, first: $first) {
                    pageInfo {
                      hasNextPage
                    }
                    edges {
                      cursor
                      listItem {
                        item {
                          __typename
                          ... on Event {
                            id
                            name
                            url
                          }
                          ... on OriginalContentStory {
                            title
                            id
                          }
                          ... on PodcastStory {
                            id
                            title
                          }
                          ... on Poster {
                            id
                            title
                          }
                          ... on Entity {
                            id
                            name
                          }
                          ... on Person {
                            id
                            fullName
                          }
                          ... on ArticleStory {
                            id
                            title
                          }

                          ... on BookStory {
                            id
                            title
                          }
                          ... on NewsletterStory {
                            id

                            title
                          }
                          ... on PodcastStory {
                            id
                            title
                          }
                          ... on OnlineCourseStory {
                            id
                            title
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `}
        variables={{ listId }}
        render={(
          readyState: ReadyState<{
            list: any;
          }>,
        ): React.ReactElement<any> => {
          let node: React.ReactElement<any>;

          if (readyState.error !== undefined && readyState.error !== null) {
            // TODO ReDO Error Messages
            node = <div className="alert alert-danger about-danger">Error Loading HeroCarousel!</div>;
          } else if (readyState.props === undefined || readyState.props === null) {
            node = <ConnectedFeaturedItemPanel />;
          } else {
            if (
              readyState.props?.list !== undefined &&
              readyState.props?.list !== null &&
              readyState.props?.list?.list !== undefined &&
              readyState.props?.list?.list !== null
            ) {
              const props: React.ComponentProps<any> = {
                ...this.props,
                list: readyState?.props?.list?.list,
              };
              node = <FeaturedItemPanelCarousel {...props} />;
            } else {
              // TODO Error Messages
              node = null;
            }
          }

          return node;
        }}
      />
    );
  }
}
